import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CopyBox } from "@/components/CopyBox";
import { Step } from "@/components/site/EmergentSetup";

const aiStudioPrompt = (tracking) => `Add this script to the <head> of EVERY page of this website, site-wide, including pages added later. Use it exactly as written: do not change, rewrite, shorten, wrap, move it to the body or remove any attribute.

${tracking}

It loads the Morr Translate language switcher. Don't add any other code, and don't change anything else on the site. When you're done, confirm the script is in the head of every page.`;

const Cell = ({ label, value, testid }) => (
  <div className="space-y-1.5 min-w-0">
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <CopyBox value={value} testid={testid} />
  </div>
);

export const GhlSetup = ({ site, onChange }) => {
  const [busy, setBusy] = useState(false);
  const conn = site.connection || {};
  const tracking = `<script src="${site.kit_url}" data-key="${site.website_key}" data-mode="ghl"></script>`;

  const check = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/sites/${site.id}/check-connection`);
      r.data.ok ? toast.success("Connected") : toast.message(r.data.detail);
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-10 max-w-4xl" data-testid="ghl-setup">
      <Step n="1" title={`Add one DNS record for es.${site.domain}`}>
        <p className="text-sm text-slate-600">Add this record where your domain's DNS is managed (GoDaddy, Namecheap, Cloudflare…).</p>
        <div className="grid grid-cols-1 sm:grid-cols-[100px_100px_1fr] gap-3 bg-white border border-slate-200 rounded-lg p-4">
          <Cell label="Type" value="CNAME" testid="dns-type" />
          <Cell label="Name" value="es" testid="dns-name" />
          <Cell label="Target" value={site.cname_target} testid="dns-target" />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Button onClick={check} disabled={busy} data-testid="cname-check-connection-button" variant="outline">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Check my connection
          </Button>
          {conn.checked_at && (
            <span data-testid="connection-status" className={`flex items-center gap-1.5 text-sm ${conn.ok ? "text-emerald-700" : "text-amber-800"}`}>
              {conn.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {conn.detail}
            </span>
          )}
        </div>
      </Step>
      <Step n="2" title="Give this prompt to GHL AI Studio">
        <div data-testid="ghl-ai-studio-section" className="space-y-3">
          <p className="text-sm text-slate-600">Copy this prompt and send it to the AI Studio builder. It already includes your website's script and key. This adds the EN | ES switcher, hreflang tags and the optional "Ver en español" banner to your English pages, and sends new text for translation.</p>
          <CopyBox value={aiStudioPrompt(tracking)} testid="ghl-ai-studio-prompt" />
          <p className="text-xs text-slate-500" data-testid="ghl-ai-studio-verify">To check it worked, open your published site in a new tab. A small <strong>EN | ES</strong> switcher should show as a tab hanging just below the header, at the top-right of every page. If it doesn't, ask AI Studio to show you the page's head code and check that the script is there exactly as written.</p>
        </div>
        <details className="group border border-slate-200 rounded-lg bg-white" data-testid="ghl-classic-tracking-section">
          <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-slate-700 hover:text-slate-900" data-testid="ghl-classic-tracking-toggle">Classic GHL site (not AI Studio)? Use the Tracking Code instead</summary>
          <div className="px-4 pb-4 space-y-3">
            <p className="text-sm text-slate-600">In GoHighLevel, go to Sites, then Settings, then Tracking Code, then Header, and paste this line.</p>
            <CopyBox value={tracking} testid="ghl-tracking-snippet" />
          </div>
        </details>
      </Step>
      <Step n="3" title="Pick Spanish versions of your forms and calendars">
        <p className="text-sm text-slate-600">GHL forms load from GHL's servers, so they can't be translated on the fly. Build a Spanish copy in GHL, then map it in <Link className="text-blue-600 underline" to={`/sites/${site.id}?tab=forms`} data-testid="goto-forms-tab">Spanish forms</Link>.</p>
      </Step>
      <p className="text-xs text-slate-500 border-t border-slate-200 pt-5">How it works: our edge server fetches your English GHL page, swaps in the stored Spanish, keeps links on <span className="font-mono">es.</span>, and caches the page close to your visitors. If the business is paused, <span className="font-mono">es.</span> redirects to the English page.</p>
    </div>
  );
};
